/**
 * CONFIGURAÇÃO CENTRALIZADA DA API PADELMASTER
 *
 * Este arquivo contém todas as configurações relacionadas ao backend:
 * - URLs base para desenvolvimento e produção
 * - Endpoints da API
 * - Configurações padrão do Axios
 * - Timeouts e headers
 */

import { CONFIG } from '@/config/environment';

// URLs base do backend
export const API_CONFIG = {
  // URL base atual (via config centralizado)
  BASE_URL: CONFIG.BACKEND_URL,
    
  // Prefixos corretos baseados na análise do backend
  AUTH_PREFIX: '/padel',  // Endpoints de autenticação existem em /padel/
  LEGACY_API_PREFIX: '/padel',  // Manter compatibilidade
};

// URL completa da API (usando endpoints corretos)
export const API_BASE_URL = `${API_CONFIG.BASE_URL}${API_CONFIG.AUTH_PREFIX}`;

// Endpoints da API organizados por categoria (baseados na análise real do backend)
export const API_ENDPOINTS = {
  // 🔐 AUTENTICAÇÃO - Endpoints Corretos
  AUTH: {
    LOGIN: `${API_CONFIG.BASE_URL}/padel/login/`,                    // POST /padel/login/
    REGISTER: `${API_CONFIG.BASE_URL}/padel/register/`,              // POST /padel/register/
    VALIDATE_TOKEN: `${API_CONFIG.BASE_URL}/padel/validate-token/`,  // POST /padel/validate-token/
    GOOGLE_AUTH: `${API_CONFIG.BASE_URL}/padel/create-or-update-user/`, // POST /padel/create-or-update-user/
    
    // Endpoints de recuperação de senha
    PASSWORD_RESET_REQUEST: `${API_CONFIG.BASE_URL}/backend/padel/password-reset/request/`, // POST /backend/padel/password-reset/request/
    PASSWORD_RESET_CONFIRM: `${API_CONFIG.BASE_URL}/backend/padel/password-reset/confirm/`, // POST /backend/padel/password-reset/confirm/
    
    // CSRF token endpoint
    CSRF_TOKEN: `${API_CONFIG.BASE_URL}/padel/get-csrf-token/`, // GET /padel/get-csrf-token/
  },

  // 📊 ESTATÍSTICAS
  STATS: {
    ALL_COUNTS: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/get-all-counts/`, // GET /padel/get-all-counts/
  },

  // 🏟️ CLUBES
  CLUBS: {
    LIST: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/list/`, // GET /padel/list/
    CREATE: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/clubs/create/`, // POST /padel/clubs/create/
    DETAIL: (slug: string) => `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/clubs/${slug}/`,
    SCHEDULE: (slug: string) => `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/clubs/${slug}/schedule/`,
    FIELDS: (slug: string) => `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/clubs/${slug}/fields/`,
    MY_CLUB: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/clubs/my-club/`,
  },

  // 🎮 JOGOS (MIXS)
  GAMES: {
    LIST: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/mixs/`,
    CREATE: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/create-mix/`, // POST /padel/create-mix/
    UPDATE: (id: number) => `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/update-mix/${id}/`,
    DELETE: (id: number) => `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/delete-mix/${id}/`,
    DETAIL: (id: number) => `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/games/${id}/`,
    UPCOMING: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/upcoming-games/`, // GET /padel/upcoming-games/
    USER_CREATED: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/user-created-games/`,
    REGISTER_TOGGLE: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/games/register-deregister/`, // POST /padel/games/register-deregister/
  },

  // 👥 USUÁRIOS
  USERS: {
    LIST: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/users/`,
    PROFILE: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/user/update/`,
  },

  // 💳 PAGAMENTOS
  PAYMENTS: {
    CREATE_CHECKOUT: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/create-checkout-session/`, // POST /padel/create-checkout-session/
    INVOICES: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/invoices/`,
  },

  // 📅 RESERVAS
  RESERVATIONS: {
    CREATE: (clubSlug: string) => `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/clubs/${clubSlug}/reservations/`, // POST /padel/clubs/{club_slug}/reservations/
    WEEKLY_AVAILABILITY: (clubSlug: string) => `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/clubs/${clubSlug}/reservations/weekly-availability/`, // GET /padel/clubs/{club_slug}/reservations/weekly-availability/
    DETAIL: (clubSlug: string, id: number) => `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/clubs/${clubSlug}/reservations/${id}/`,
    DELETE: (clubSlug: string, id: number) => `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/clubs/${clubSlug}/reservations/${id}/`,
  },

  // 🏆 TORNEIOS
  TOURNAMENTS: {
    LIST: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/tournaments/`,
    CREATE: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/tournaments/create/`,
    ADD_PLAYERS_SCORES: `${API_CONFIG.BASE_URL}${API_CONFIG.LEGACY_API_PREFIX}/tournaments/add-players-scores/`,
  },
};

// Configurações padrão para requisições Axios
export const AXIOS_CONFIG = {
  // Headers padrão (removendo headers problemáticos para CORS)
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    // Remover x-frontend-version que está causando erro de CORS
    // Headers customizados serão adicionados apenas quando necessário
  },
  
  // Timeout otimizado para desenvolvimento vs produção
  timeout: import.meta.env.DEV ? 5000 : 30000,

  // Cookies apenas em produção para evitar problemas de CORS
  withCredentials: !import.meta.env.DEV,
};

// Configurações específicas para desenvolvimento
export const DEV_CONFIG = {
  // Proxy para desenvolvimento (usado pelo Vite)
  PROXY_PREFIX: '/api',
  
  // Logs detalhados em desenvolvimento
  ENABLE_LOGS: import.meta.env.VITE_DEBUG_API === 'true',
  
  // Mock data em caso de falhas
  USE_MOCK_DATA: import.meta.env.VITE_ENABLE_MOCK_DATA === 'true',
  
  // Fallback automático
  AUTO_FALLBACK: true,
};

// Exportar URL base para compatibilidade com código legado
export { API_BASE_URL as default };
